// menuApi.js
// Menu data layer. Reads from the shared menuData.js (single source of truth).
// Talks to API Gateway when the menu endpoints exist; otherwise falls back
// to the shared menu so the Kitchen and Guest pages always work.

import { MENU } from './menuData';
import { MENU_IMAGES } from './menuImages';

const API_BASE = 'https://z51smv2b7g.execute-api.ap-southeast-2.amazonaws.com/prod';
const MENU_SYNC_CHANNEL = 'gapgo-menu-sync';

// Staff session. The old REACT_APP_STAFF_KEY was a build-time constant, which
// meant the "secret" shipped inside the JS bundle every guest downloads — it
// protected nothing. The access code is now checked server-side and exchanged
// for a short-lived token, sent in the request BODY (a custom header would fail
// the fixed CORS preflight on /menu, and a query param would land in access logs).
const TOKEN_KEY = 'gapgo_staff_token';
const TOKEN_EXP_KEY = 'gapgo_staff_token_exp';

// Super-admin session, kept in its own key so signing out of one does not sign
// you out of the other, and so a staff token can never be mistaken for an admin
// one. Same reasoning as above, one step further: the admin username and password
// used to be REACT_APP_* values, which CRA bakes into the public bundle — they
// were readable by any visitor. They are now checked server-side against a hash
// that only exists in Lambda env.
const ADMIN_TOKEN_KEY = 'gapgo_admin_token';
const ADMIN_TOKEN_EXP_KEY = 'gapgo_admin_token_exp';

export function getStaffToken() {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const exp = Number(localStorage.getItem(TOKEN_EXP_KEY) || 0);
    if (!token || !exp || Date.now() > exp) return null;
    return token;
  } catch (e) {
    return null;
  }
}

export function isStaffAuthed() {
  return Boolean(getStaffToken());
}

export function clearStaffSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXP_KEY);
    localStorage.removeItem('staffAuth');
    localStorage.removeItem('staffName');
    // Otherwise the next sign-in on a shared tablet briefly shows the previous
    // restaurant's name before the new token lands.
    localStorage.removeItem('staffRestaurantId');
    localStorage.removeItem('staffRestaurantName');
  } catch (e) {
    // ignore
  }
}

export function getAdminToken() {
  try {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    const exp = Number(localStorage.getItem(ADMIN_TOKEN_EXP_KEY) || 0);
    if (!token || !exp || Date.now() > exp) return null;
    return token;
  } catch (e) {
    return null;
  }
}

/**
 * Is this browser holding a live admin session?
 *
 * Reads the ROLE out of the signed token payload rather than trusting a
 * localStorage flag. The old gate was `localStorage.adminAuth === 'true'`, which
 * anyone could set from devtools — and because admin data calls authenticate with
 * the STAFF token, any kitchen worker could promote themselves to super admin that
 * way. The payload here is only a UX check; the signature is what actually counts
 * and the server re-verifies it on every admin call.
 */
export function isAdminAuthed() {
  const token = getAdminToken();
  if (!token) return false;
  try {
    const [payload] = String(token).split('.');
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return json.r === 'admin' && Number(json.exp) > Date.now();
  } catch (e) {
    return false;
  }
}

export function clearAdminSession() {
  try {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    localStorage.removeItem(ADMIN_TOKEN_EXP_KEY);
    localStorage.removeItem('adminAuth'); // retire the old bypassable flag
  } catch (e) {
    // ignore
  }
}

export async function adminLogin(username, password) {
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'adminLogin', username, password }),
  });
  if (!res.ok) {
    const msg =
      res.status === 401 ? 'Invalid credentials'
        : res.status === 503 ? 'Admin sign-in is not configured yet'
          : 'Sign-in unavailable — try again';
    throw new Error(msg);
  }
  const data = await res.json();
  try {
    localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
    localStorage.setItem(ADMIN_TOKEN_EXP_KEY, String(Date.now() + (data.expiresIn || 0)));
    localStorage.removeItem('adminAuth');
  } catch (e) {
    // ignore storage failures; the token still works for this page view
  }
  return data;
}

/**
 * Admin-only calls. These send the ADMIN token, not the staff one — the Lambda
 * checks `isAdmin()` server-side, so a kitchen token gets 401 here no matter what
 * the browser sends.
 */
async function adminPost(payload) {
  const token = getAdminToken();
  if (!token) throw new Error('Not signed in as admin');
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, token }),
  });
  if (!res.ok) {
    const msg = res.status === 401 ? 'Admin session expired — sign in again'
      : res.status === 404 ? 'No such restaurant'
        : 'Request failed — try again';
    throw new Error(msg);
  }
  return res.json();
}

/** Restaurants with their access-code state. Never returns a code or a hash. */
export async function adminListRestaurants() {
  const data = await adminPost({ action: 'adminListRestaurants' });
  return Array.isArray(data.restaurants) ? data.restaurants : [];
}

/**
 * Issues a NEW access code and returns it ONCE. It is stored only as a hash, so
 * if the admin closes the dialog without copying it, the only way forward is to
 * regenerate again. Every existing session for that restaurant dies immediately.
 */
export async function adminRegenerateCode(restaurantId) {
  return adminPost({ action: 'adminRegenerateCode', restaurantId });
}

/** Logs every tablet out WITHOUT changing the code — for a lost or stolen device. */
export async function adminRevokeSessions(restaurantId) {
  return adminPost({ action: 'adminRevokeSessions', restaurantId });
}

export async function staffLogin(pin, name) {
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffLogin', pin, name }),
  });
  if (!res.ok) {
    const msg = res.status === 401 ? 'Invalid access code' : 'Sign-in unavailable — try again';
    throw new Error(msg);
  }
  const data = await res.json();
  try {
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + (data.expiresIn || 0)));
    localStorage.setItem('staffAuth', 'true');
    if (name) localStorage.setItem('staffName', name);
    // Which restaurant this code belongs to. Display only — the kitchen labels
    // itself with this, but which ORDERS it can see is derived server-side from
    // the signed token, never from these values.
    if (data.restaurantId) localStorage.setItem('staffRestaurantId', data.restaurantId);
    else localStorage.removeItem('staffRestaurantId');
    if (data.restaurantName) localStorage.setItem('staffRestaurantName', data.restaurantName);
    else localStorage.removeItem('staffRestaurantName');
  } catch (e) {
    // ignore storage failures; the token still works for this page view
  }
  return data;
}

/**
 * A restaurant changes its OWN access code from inside the kitchen.
 *
 * Which restaurant is affected comes from the signed token server-side, never from
 * here — this call cannot touch anyone else's code. The current code is required
 * as well as the session, because a signed-in tablet left on a counter is the
 * realistic threat. On success every OTHER device is signed out and this one gets
 * a fresh token, which we swap in immediately so the person who just changed it
 * is not thrown out of the screen they are standing at.
 */
export async function staffSetCode(currentCode, newCode) {
  const token = getStaffToken();
  if (!token) throw new Error('Please sign in again');
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffSetCode', token, currentCode, newCode }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not change the code — try again');
  try {
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + (data.expiresIn || 0)));
  } catch (e) {
    // ignore storage failures; the new token still works for this page view
  }
  return data;
}

/**
 * Restaurant sign-in: name-or-email + password.
 *
 * `remember` defaults to true and returns a 90-day device token, because the
 * complaint this whole change answers was re-typing a secret every shift. Pass
 * false on a shared or public screen to get a 12-hour session instead.
 */
export async function restaurantLogin(identifier, password, remember = true) {
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'restaurantLogin', identifier, password, remember, label: deviceLabel() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Sign-in failed — try again');
  try {
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + (data.expiresIn || 0)));
    localStorage.setItem('staffAuth', 'true');
    if (data.restaurantId) localStorage.setItem('staffRestaurantId', data.restaurantId);
    if (data.restaurantName) localStorage.setItem('staffRestaurantName', data.restaurantName);
    if (data.deviceId) localStorage.setItem('gapgo_device_id', data.deviceId);
  } catch (e) {
    // ignore storage failures; the token still works for this page view
  }
  return data;
}

/** A restaurant changes its own password from the kitchen. */
export async function staffSetPassword(currentPassword, newPassword) {
  const token = getStaffToken();
  if (!token) throw new Error('Please sign in again');
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffSetPassword', token, currentPassword, newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not change the password');
  return data;
}

/** Admin: set or reset a restaurant's password. Never returns it. */
export async function adminSetRestaurantPassword(restaurantId, password) {
  return adminPost({ action: 'adminSetRestaurantPassword', restaurantId, password });
}

/**
 * Forgot password, step 1: ask for a code.
 *
 * ALWAYS resolves, even for a restaurant that does not exist — the server replies
 * 200 either way so this endpoint cannot be used to enumerate RedSpeed's customers,
 * and the client must not undo that by surfacing a difference.
 */
export async function requestPasswordReset(identifier) {
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffRequestPasswordReset', identifier }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok && res.status === 503) throw new Error('Password reset is not configured yet');
  return data;
}

/** Forgot password, step 2: redeem the code and set a new password. */
export async function confirmPasswordReset(identifier, code, newPassword) {
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffConfirmPasswordReset', identifier, code, newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not reset the password');
  return data;
}

/** A restaurant unpairs every one of its own devices. Signs this screen out too. */
export async function staffUnpairAllDevices() {
  const token = getStaffToken();
  if (!token) throw new Error('Please sign in again');
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffUnpairAllDevices', token }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not sign out the devices');
  return data;
}

/** Service hours for this restaurant, as the kitchen sees them. */
export async function staffGetPauseRules() {
  const token = getStaffToken();
  if (!token) throw new Error('Please sign in again');
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffGetPauseRules', token }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not load your opening hours');
  return data.pause;
}

/** Save service hours. These reach guests on their next menu load. */
export async function staffSetPauseRules(rules) {
  const token = getStaffToken();
  if (!token) throw new Error('Please sign in again');
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffSetPauseRules', token, ...rules }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not save your opening hours');
  return data.pause;
}

/**
 * Managed menu categories.
 *
 * Best-effort by design: the actions are new, so a tablet whose Lambda predates them
 * gets a 4xx here. Rather than break the menu screen, fall back to deriving the list
 * from the items already on screen — which is exactly what the server does on a
 * restaurant that has never saved a category list.
 */
export async function listCategories(hotelId) {
  const token = getStaffToken();
  if (!token) return [];
  try {
    const res = await fetch(API_BASE + '/menu', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'staffListCategories', token, hotelId }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data.categories) ? data.categories : [];
  } catch (e) {
    return [];
  }
}

export async function saveCategories(hotelId, categories) {
  const token = getStaffToken();
  if (!token) throw new Error('Please sign in again');
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffSetCategories', token, hotelId, categories }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not save the categories');
  return data;
}

/** Human-readable device name, so an owner revoking one is not guessing. */
function deviceLabel() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return 'Android tablet';
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';
  return 'Kitchen screen';
}

/**
 * Just enough to brand the pairing screen before anyone has proved anything.
 * Public by design, and the Lambda returns no hash, no email and no Stripe account.
 */
export async function getRestaurantPublic(restaurantId) {
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'restaurantPublic', restaurantId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not find that restaurant');
  return data.restaurant;
}

/**
 * Pair this device once. The token that comes back lasts 90 days, so the tablet
 * on the pass never signs in again — which is the entire point. Stored in the same
 * keys as a staff session, so every existing screen and guard works unchanged.
 */
export async function pairDevice(restaurantId, code, label) {
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'pairDevice', restaurantId, code, label }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not pair this device');
  try {
    localStorage.setItem(TOKEN_KEY, data.token);
    localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + (data.expiresIn || 0)));
    localStorage.setItem('staffAuth', 'true');
    if (data.restaurantId) localStorage.setItem('staffRestaurantId', data.restaurantId);
    if (data.restaurantName) localStorage.setItem('staffRestaurantName', data.restaurantName);
    if (data.deviceId) localStorage.setItem('gapgo_device_id', data.deviceId);
  } catch (e) {
    // ignore storage failures; the token still works for this page view
  }
  return data;
}

/** Admin: hand a restaurant a one-time code to pair a tablet with. Shown once. */
export async function adminIssuePairingCode(restaurantId) {
  return adminPost({ action: 'adminIssuePairingCode', restaurantId });
}

export async function adminListDevices(restaurantId) {
  const data = await adminPost({ action: 'adminListDevices', restaurantId });
  return data;
}

/** The "that tablet walked off" button. Everything else keeps working. */
export async function adminRevokeDevice(restaurantId, deviceId) {
  return adminPost({ action: 'adminRevokeDevice', restaurantId, deviceId });
}

export async function adminUnpairAllDevices(restaurantId) {
  return adminPost({ action: 'adminUnpairAllDevices', restaurantId });
}

/**
 * A restaurant's own account — where our mail goes, and how payouts stand.
 *
 * Same rule as staffSetCode: which restaurant this reads and writes comes off the
 * signed token server-side. Passing a restaurantId from here would be ignored.
 */
export async function staffGetAccount() {
  const token = getStaffToken();
  if (!token) throw new Error('Please sign in again');
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffGetAccount', token }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not load your account');
  return data.account;
}

/** Send only the fields being changed — omitting one leaves it alone. */
export async function staffSetAccountEmails(patch) {
  const token = getStaffToken();
  if (!token) throw new Error('Please sign in again');
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'staffSetAccountEmails', token, ...patch }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not save those addresses');
  return data.account;
}

/** Same write, from the RedSpeed side, for a restaurant that rings us instead. */
export async function adminSetAccountEmails(restaurantId, patch) {
  const data = await adminPost({ action: 'adminSetAccountEmails', restaurantId, ...patch });
  return data.account;
}

/** Display label for the signed-in restaurant, or null on a legacy shared-code session. */
export function getStaffRestaurant() {
  try {
    const id = localStorage.getItem('staffRestaurantId');
    if (!id) return null;
    return { restaurantId: id, name: localStorage.getItem('staffRestaurantName') || id };
  } catch (e) {
    return null;
  }
}

const withToken = (body) => {
  const token = getStaffToken();
  return token ? { ...body, token } : body;
};

// Resize in the browser before upload. Keeps the payload inside the API Gateway
// limit without a presign/CORS dance, and matches the 700px the shipped photos
// use so uploaded and built-in images look consistent on the menu.
function resizeToJpeg(file, maxEdge = 700, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file isn't an image we can read"));
    };
    img.src = url;
  });
}

export async function uploadMenuPhoto(hotelId, itemId, file) {
  if (!getStaffToken()) throw new Error('Your session expired — sign in again');
  const data = await resizeToJpeg(file);
  const res = await fetch(API_BASE + '/menu', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(withToken({ action: 'photoUpload', hotelId, itemId, data })),
  });
  if (!res.ok) {
    let msg = 'Upload failed';
    try {
      msg = (await res.json()).error || msg;
    } catch (e) {
      // keep default
    }
    throw new Error(msg);
  }
  return res.json();
}

function notifyMenuChanged(hotelId) {
  try {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('gapgo-menu-updated', { detail: { hotelId } }));
    if ('BroadcastChannel' in window) {
      const channel = new BroadcastChannel(MENU_SYNC_CHANNEL);
      channel.postMessage({ hotelId, at: Date.now() });
      channel.close();
    }
  } catch (e) {
    // ignore broadcast failures and keep storage fallback behavior
  }
}

function getMenuStorageKey(hotelId) {
  return 'gapgo_menu_' + (hotelId || 'default');
}

function normalizeMenuItem(item) {
  const id = item.id || item.itemId || `menu-${Date.now().toString(36)}`;
  return {
    ...item,
    id,
    itemId: id,
    inStock: item.inStock !== false,
    price: Number(item.price || 0),
    category: item.category || 'Uncategorized',
    name: item.name || 'Item',
    description: item.description || item.desc || '',
    emoji: item.emoji || '🍽️',
    // The live menu endpoint has no image field yet. Enrich Darcy's stable menu
    // IDs here, while leaving restaurant-uploaded images free to override them.
    image: item.image || MENU_IMAGES[id] || '',
  };
}

function readStoredMenu(hotelId) {
  try {
    const raw = localStorage.getItem(getMenuStorageKey(hotelId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map(normalizeMenuItem);
    }
    if (parsed && typeof parsed === 'object') {
      const seed = MENU.map((item) => {
        const ov = parsed[item.id];
        return ov == null ? normalizeMenuItem(item) : normalizeMenuItem({ ...item, inStock: ov });
      });
      writeStoredMenu(hotelId, seed);
      return seed;
    }
    return null;
  } catch (e) {
    return null;
  }
}

function writeStoredMenu(hotelId, menu) {
  try {
    localStorage.setItem(getMenuStorageKey(hotelId), JSON.stringify(menu.map(normalizeMenuItem)));
    notifyMenuChanged(hotelId);
  } catch (e) {
    // ignore storage failures and keep API/local fallback behavior
  }
}

function mergeSeedMenu(hotelId) {
  const overrides = readStoredMenu(hotelId);
  if (overrides) return overrides;
  return MENU.map((item) => normalizeMenuItem(item));
}

export const SEED_MENU = MENU;

/**
 * @param hotelId       which venue's menu (prices and stock differ per hotel)
 * @param restaurantId  optional. Switches the server from a full-table Scan to an
 *                      indexed Query. Sent ALONGSIDE hotelId, never instead of it —
 *                      the server falls back to the Scan if the index cannot answer
 *                      (not built yet, or the backfill has not reached those rows),
 *                      and that fallback needs the hotel.
 */
/**
 * Menu AND the service hours that govern it, in one call.
 *
 * The guest page needs both, and fetching them separately meant it could render an
 * orderable menu for a restaurant that had closed. They arrive together so that is
 * not possible.
 *
 * `pause` is `null` when the server is too old to send it (or no restaurantId was
 * given), which callers must treat as "no restrictions known" rather than "open".
 */
export async function getMenuBundle(hotelId, restaurantId) {
  const items = await getMenu(hotelId, restaurantId);
  return { items, pause: lastPause };
}

/** Set by getMenu from the most recent successful response. */
let lastPause = null;

export async function getMenu(hotelId, restaurantId) {
  const stored = readStoredMenu(hotelId);
  try {
    const qs = new URLSearchParams({ hotelId });
    if (restaurantId) qs.set('restaurantId', restaurantId);
    const res = await fetch(API_BASE + '/menu?' + qs.toString(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error('menu endpoint not ready');
    const data = await res.json();
    // Service hours ride along with the menu — see getMenuBundle.
    lastPause = (data && data.pause) || null;
    const remote = Array.isArray(data)
      ? data
      : data && Array.isArray(data.items)
        ? data.items
        : [];
    if (remote.length) {
      const seeded = remote.map(normalizeMenuItem);
      if (!stored || !stored.length) return seeded;
      const merged = [...seeded];
      stored.forEach((item) => {
        const key = item.id || item.itemId;
        const idx = merged.findIndex((entry) => (entry.id || entry.itemId) === key);
        if (idx >= 0) merged[idx] = normalizeMenuItem({ ...merged[idx], ...item, id: key });
        else merged.push(normalizeMenuItem(item));
      });
      return merged;
    }
    throw new Error('empty menu');
  } catch (e) {
    return stored && stored.length ? stored : mergeSeedMenu(hotelId);
  }
}

export async function updateMenuItem(hotelId, item) {
  const key = item.id || item.itemId;
  const current = readStoredMenu(hotelId) || MENU.map((entry) => normalizeMenuItem(entry));
  const nextItem = normalizeMenuItem({ ...item, id: key });
  const nextMenu = current.some((entry) => (entry.id || entry.itemId) === key)
    ? current.map((entry) => ((entry.id || entry.itemId) === key ? { ...entry, ...nextItem } : entry))
    : [...current, nextItem];
  writeStoredMenu(hotelId, nextMenu);

  try {
    const res = await fetch(API_BASE + '/menu', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withToken({ hotelId, item })),
    });
    if (!res.ok) throw new Error('menu update endpoint not ready');
    return await res.json();
  } catch (e) {
    return { persisted: false };
  }
}

export async function removeMenuItem(hotelId, itemId) {
  const current = readStoredMenu(hotelId) || MENU.map((entry) => normalizeMenuItem(entry));
  const nextMenu = current.filter((entry) => (entry.id || entry.itemId) !== itemId);
  writeStoredMenu(hotelId, nextMenu);

  try {
    const res = await fetch(API_BASE + '/menu', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(withToken({ hotelId, itemId })),
    });
    if (!res.ok) throw new Error('menu delete failed');
    return res.json();
  } catch (e) {
    return { persisted: false };
  }
}

const menuApi = { getMenu, updateMenuItem, removeMenuItem, SEED_MENU };

export default menuApi;
